import { useState } from 'react'

import type { PreviewDocument } from '@/components/AgentChat/previewDocument'
import { DocumentMetadataDrawer } from './DocumentMetadataDrawer'
import { DocumentPreviewDialog } from './DocumentPreviewDialog'
import { RenameDocumentDialog } from './RenameDocumentDialog'
import type { DocumentsTableDocument } from './types'

/**
 * The Details drawer, the rename dialog and the file preview, hoisted out of the rows.
 *
 * Mounted once per table rather than once per row: a drawer per row is a drawer
 * per document, and the rows are lazily expanded groups that would unmount it
 * mid-animation. Render `dialogs` alongside the table and hand the rows
 * `openDetails` / `openRename` / `openPreview`.
 */
export function useDocumentRowActions() {
  // The target outlives the close so the drawer has content to animate out with.
  const [detailsTarget, setDetailsTarget] = useState<DocumentsTableDocument | null>(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [renameTarget, setRenameTarget] = useState<DocumentsTableDocument | null>(null)
  const [previewTarget, setPreviewTarget] = useState<{ doc: PreviewDocument; title: string } | null>(null)

  const openDetails = (document: DocumentsTableDocument) => {
    setDetailsTarget(document)
    setIsDetailsOpen(true)
  }

  const dialogs = (
    <>
      <DocumentMetadataDrawer
        orgId={detailsTarget?.org}
        document={detailsTarget}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
      />
      {renameTarget?.org && (
        <RenameDocumentDialog
          orgId={renameTarget.org}
          document={renameTarget}
          onClose={() => setRenameTarget(null)}
        />
      )}
      {previewTarget && (
        <DocumentPreviewDialog
          doc={previewTarget.doc}
          title={previewTarget.title}
          onOpenChange={() => setPreviewTarget(null)}
        />
      )}
    </>
  )

  return {
    openDetails,
    openRename: setRenameTarget,
    openPreview: (doc: PreviewDocument, title: string) => setPreviewTarget({ doc, title }),
    dialogs,
  }
}

export interface DocumentRowActions {
  openDetails: (document: DocumentsTableDocument) => void
  /** Absent in a read-only surface — the row then offers Details only. */
  openRename?: (document: DocumentsTableDocument) => void
  /** Staff only: the preview reads the file through an internal endpoint. Absent, a file link just opens the file. */
  openPreview?: (doc: PreviewDocument, title: string) => void
}
